import React, { type ReactNode, useEffect, useState } from "react";
import { ApiClientError } from "../../api";
import AutoDashboard from "./AutoDashboard";
import PeriodControls, { defaultPeriod, periodQuery, type PeriodState } from "./PeriodControls";

// Shared plumbing for every generic MVP-E dashboard tab: period controls
// (+ compare, test #29), fetch/loading/error, and the generic renderer.
// Tabs that need extra filters (seller/professional pickers) pass
// `extraParams`/`extraControls`; tabs that need bespoke UI (Alerts,
// Forecast) skip this and build their own.
export default function DashboardTab({
  fetcher,
  hideKeys,
  extraParams,
  extraControls,
  allowCompare = true,
  emptyLabel,
}: {
  fetcher: (params: Record<string, string | undefined>) => Promise<Record<string, any>>;
  hideKeys?: string[];
  extraParams?: Record<string, string | undefined>;
  extraControls?: ReactNode;
  allowCompare?: boolean;
  emptyLabel?: string;
}) {
  const [period, setPeriod] = useState<PeriodState>(defaultPeriod());
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher({ ...periodQuery(period), ...extraParams })
      .then((d) => !cancelled && setData(d))
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        setError(e instanceof ApiClientError ? e.message : "No se pudo cargar este panel.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.from, period.to, period.compareEnabled, period.compareFrom, period.compareTo, JSON.stringify(extraParams)]);

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <PeriodControls period={period} onChange={setPeriod} allowCompare={allowCompare} />
        {extraControls}
      </div>

      {loading && <div className="loading-strip">Cargando métricas...</div>}
      {error && <p className="form-error">{error}</p>}

      {!loading && !error && data && (
        <>
          <AutoDashboard data={data} hideKeys={hideKeys} emptyLabel={emptyLabel} />
          {data.comparison && (
            <div className="section-card">
              <h2>Período de comparación ({period.compareFrom} → {period.compareTo})</h2>
              <AutoDashboard data={data.comparison} hideKeys={hideKeys} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
